"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Save,
  DollarSign,
  Building2,
  CreditCard,
  Settings as SettingsIcon,
  AlertCircle,
  Loader2,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useInitializeSettingsMutation,
} from "@/store";
import { toast } from "sonner";

export function AdminSettingsScreen() {
  const [activeTab, setActiveTab] = useState("pricing");
  const { data: settingsData, isLoading, error } = useGetSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] = useUpdateSettingsMutation();
  const [initializeSettings, { isLoading: isInitializing }] = useInitializeSettingsMutation();

  // Form state
  const [pricingSettings, setPricingSettings] = useState({
    deliveryFee: "0",
    taxRate: "0",
  });

  const [businessSettings, setBusinessSettings] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    website: "",
    socialMedia: "{}",
    businessHours: "{}",
    description: "",
  });

  const [paymentSettings, setPaymentSettings] = useState({
    paystackEnabled: false,
    flutterwaveEnabled: false,
  });

  const [legalSettings, setLegalSettings] = useState({
    privacyPolicy: "",
    termsOfService: "",
    refundPolicy: "",
  });

  // Load settings into form state
  useEffect(() => {
    if (settingsData?.data) {
      const settings = settingsData.data;

      // Pricing settings
      const pricing = settings.pricing || [];
      const deliveryFee = pricing.find((s) => s.key === "delivery_fee")?.value || "0";
      const taxRate = pricing.find((s) => s.key === "tax_rate")?.value || "0";
      setPricingSettings({ deliveryFee, taxRate });

      // Business settings
      const business = settings.business || [];
      setBusinessSettings({
        name: business.find((s) => s.key === "business_name")?.value || "",
        email: business.find((s) => s.key === "business_email")?.value || "",
        phone: business.find((s) => s.key === "business_phone")?.value || "",
        address: business.find((s) => s.key === "business_address")?.value || "",
        website: business.find((s) => s.key === "business_website")?.value || "",
        socialMedia: business.find((s) => s.key === "business_social_media")?.value || "{}",
        businessHours: business.find((s) => s.key === "business_hours")?.value || "{}",
        description: business.find((s) => s.key === "business_description")?.value || "",
      });

      // Payment settings
      const payment = settings.payment || [];
      setPaymentSettings({
        paystackEnabled: payment.find((s) => s.key === "paystack_enabled")?.value === "true",
        flutterwaveEnabled: payment.find((s) => s.key === "flutterwave_enabled")?.value === "true",
      });

      // Legal settings
      const legal = settings.legal || [];
      setLegalSettings({
        privacyPolicy: legal.find((s) => s.key === "privacy_policy")?.value || "",
        termsOfService: legal.find((s) => s.key === "terms_of_service")?.value || "",
        refundPolicy: legal.find((s) => s.key === "refund_policy")?.value || "",
      });
    }
  }, [settingsData]);

  // Initialize settings if not found
  useEffect(() => {
    if (error && (error as any).status === 404) {
      // Settings not initialized, offer to initialize
      const shouldInit = window.confirm(
        "Settings have not been initialized. Would you like to initialize default settings?"
      );
      if (shouldInit) {
        initializeSettings();
      }
    }
  }, [error, initializeSettings]);

  const handleSave = async () => {
    try {
      const updates: Array<{ key: string; value: string }> = [];

      // Pricing updates
      updates.push(
        { key: "delivery_fee", value: pricingSettings.deliveryFee },
        { key: "tax_rate", value: pricingSettings.taxRate }
      );

      // Business updates
      updates.push(
        { key: "business_name", value: businessSettings.name },
        { key: "business_email", value: businessSettings.email },
        { key: "business_phone", value: businessSettings.phone },
        { key: "business_address", value: businessSettings.address },
        { key: "business_website", value: businessSettings.website },
        { key: "business_social_media", value: businessSettings.socialMedia },
        { key: "business_hours", value: businessSettings.businessHours },
        { key: "business_description", value: businessSettings.description }
      );

      // Payment updates
      updates.push(
        { key: "paystack_enabled", value: paymentSettings.paystackEnabled ? "true" : "false" },
        { key: "flutterwave_enabled", value: paymentSettings.flutterwaveEnabled ? "true" : "false" }
      );

      // Legal updates
      updates.push(
        { key: "privacy_policy", value: legalSettings.privacyPolicy },
        { key: "terms_of_service", value: legalSettings.termsOfService },
        { key: "refund_policy", value: legalSettings.refundPolicy }
      );

      await updateSettings({ updates }).unwrap();
      toast.success("Settings saved successfully");
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to save settings");
    }
  };

  const taxRatePercent = parseFloat(pricingSettings.taxRate) * 100;
  const deliveryFeeNum = parseFloat(pricingSettings.deliveryFee) || 0;

  // Calculate example total
  const exampleSubtotal = 5000;
  const exampleTax = exampleSubtotal * parseFloat(pricingSettings.taxRate);
  const exampleTotal = exampleSubtotal + exampleTax + deliveryFeeNum;

  if (isLoading || isInitializing) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Settings</h1>
            <p className="text-muted-foreground">
              Manage application settings and configuration
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={isUpdating}
            size="lg"
            className="gap-2"
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="pricing" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="business" className="gap-2">
              <Building2 className="h-4 w-4" />
              Business Info
            </TabsTrigger>
            <TabsTrigger value="payment" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Payment
            </TabsTrigger>
            <TabsTrigger value="legal" className="gap-2">
              <FileText className="h-4 w-4" />
              Legal
            </TabsTrigger>
            <TabsTrigger value="general" className="gap-2">
              <SettingsIcon className="h-4 w-4" />
              General
            </TabsTrigger>
          </TabsList>

          {/* Pricing Settings Tab */}
          <TabsContent value="pricing" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Pricing Settings</CardTitle>
                <CardDescription>
                  Configure delivery fees and tax rates for orders
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Delivery Fee */}
                <div className="space-y-2">
                  <Label htmlFor="delivery_fee">Delivery Fee (₦)</Label>
                  <Input
                    id="delivery_fee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={pricingSettings.deliveryFee}
                    onChange={(e) =>
                      setPricingSettings({
                        ...pricingSettings,
                        deliveryFee: e.target.value,
                      })
                    }
                    placeholder="0"
                  />
                  <p className="text-sm text-muted-foreground">
                    Set to 0 for free delivery
                  </p>
                </div>

                {/* Tax Rate */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="tax_rate">Tax Rate</Label>
                    <span className="text-lg font-semibold">{taxRatePercent.toFixed(1)}%</span>
                  </div>
                  <Slider
                    id="tax_rate"
                    min={0}
                    max={100}
                    step={0.1}
                    value={[taxRatePercent]}
                    onValueChange={([value]) =>
                      setPricingSettings({
                        ...pricingSettings,
                        taxRate: (value / 100).toString(),
                      })
                    }
                    className="w-full"
                  />
                  <p className="text-sm text-muted-foreground">
                    Adjust the tax rate from 0% to 100%
                  </p>
                </div>

                {/* Preview */}
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Price Calculation Preview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal:</span>
                      <span>₦{exampleSubtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax ({taxRatePercent.toFixed(1)}%):</span>
                      <span>₦{exampleTax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Delivery Fee:</span>
                      <span>₦{deliveryFeeNum.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                      <span>Total:</span>
                      <span>₦{exampleTotal.toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Information Tab */}
          <TabsContent value="business" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
                <CardDescription>
                  Manage your business details displayed to customers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      value={businessSettings.name}
                      onChange={(e) =>
                        setBusinessSettings({
                          ...businessSettings,
                          name: e.target.value,
                        })
                      }
                      placeholder="Zest and Zeal"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_email">Email *</Label>
                    <Input
                      id="business_email"
                      type="email"
                      value={businessSettings.email}
                      onChange={(e) =>
                        setBusinessSettings({
                          ...businessSettings,
                          email: e.target.value,
                        })
                      }
                      placeholder="hello@zestandzeal.com.ng"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_phone">Phone</Label>
                    <Input
                      id="business_phone"
                      type="tel"
                      value={businessSettings.phone}
                      onChange={(e) =>
                        setBusinessSettings({
                          ...businessSettings,
                          phone: e.target.value,
                        })
                      }
                      placeholder="+234 815 758 3086"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_website">Website</Label>
                    <Input
                      id="business_website"
                      type="url"
                      value={businessSettings.website}
                      onChange={(e) =>
                        setBusinessSettings({
                          ...businessSettings,
                          website: e.target.value,
                        })
                      }
                      placeholder="https://zestandzeal.com.ng"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_address">Address</Label>
                  <Textarea
                    id="business_address"
                    value={businessSettings.address}
                    onChange={(e) =>
                      setBusinessSettings({
                        ...businessSettings,
                        address: e.target.value,
                      })
                    }
                    placeholder="123 Urban Street, Food District, Lagos, Nigeria"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_description">Description</Label>
                  <Textarea
                    id="business_description"
                    value={businessSettings.description}
                    onChange={(e) =>
                      setBusinessSettings({
                        ...businessSettings,
                        description: e.target.value,
                      })
                    }
                    placeholder="Brief description of your business"
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_social_media">Social Media (JSON)</Label>
                  <Textarea
                    id="business_social_media"
                    value={businessSettings.socialMedia}
                    onChange={(e) =>
                      setBusinessSettings({
                        ...businessSettings,
                        socialMedia: e.target.value,
                      })
                    }
                    placeholder='{"facebook": "https://facebook.com/...", "instagram": "https://instagram.com/...", "twitter": "https://twitter.com/...", "snapchat": "https://snapchat.com/add/...", "tiktok": "https://tiktok.com/@..."}'
                    rows={3}
                  />
                  <p className="text-sm text-muted-foreground">
                    JSON object with social media links (facebook, instagram, twitter, snapchat, tiktok)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_hours">Business Hours (JSON)</Label>
                  <Textarea
                    id="business_hours"
                    value={businessSettings.businessHours}
                    onChange={(e) =>
                      setBusinessSettings({
                        ...businessSettings,
                        businessHours: e.target.value,
                      })
                    }
                    placeholder='{"monday": "9am - 6pm", "tuesday": "9am - 6pm", ...}'
                    rows={3}
                  />
                  <p className="text-sm text-muted-foreground">
                    JSON object with business hours by day
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Providers Tab */}
          <TabsContent value="payment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Providers</CardTitle>
                <CardDescription>
                  Enable or disable payment providers. Providers must be configured in environment variables to be enabled.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Paystack */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">Paystack</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Pay with card, bank transfer, or USSD
                    </p>
                  </div>
                  <Switch
                    checked={paymentSettings.paystackEnabled}
                    onCheckedChange={(checked) =>
                      setPaymentSettings({
                        ...paymentSettings,
                        paystackEnabled: checked,
                      })
                    }
                  />
                </div>

                {/* Flutterwave */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">Flutterwave</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Pay with card, bank transfer, or mobile money
                    </p>
                  </div>
                  <Switch
                    checked={paymentSettings.flutterwaveEnabled}
                    onCheckedChange={(checked) =>
                      setPaymentSettings({
                        ...paymentSettings,
                        flutterwaveEnabled: checked,
                      })
                    }
                  />
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Payment providers must have their secret keys configured in environment variables before they can be enabled.
                    Only enabled providers will appear as payment options during checkout.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Legal Settings Tab */}
          <TabsContent value="legal" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Legal Pages</CardTitle>
                <CardDescription>
                  Manage content for privacy policy, terms of service, and refund policy pages
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Privacy Policy */}
                <div className="space-y-2">
                  <Label htmlFor="privacy_policy">Privacy Policy</Label>
                  <Textarea
                    id="privacy_policy"
                    placeholder="Enter your privacy policy content here..."
                    value={legalSettings.privacyPolicy}
                    onChange={(e) =>
                      setLegalSettings({
                        ...legalSettings,
                        privacyPolicy: e.target.value,
                      })
                    }
                    className="min-h-[200px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    HTML content for the privacy policy page. Include proper HTML tags for formatting.
                  </p>
                </div>

                {/* Terms of Service */}
                <div className="space-y-2">
                  <Label htmlFor="terms_of_service">Terms of Service</Label>
                  <Textarea
                    id="terms_of_service"
                    placeholder="Enter your terms of service content here..."
                    value={legalSettings.termsOfService}
                    onChange={(e) =>
                      setLegalSettings({
                        ...legalSettings,
                        termsOfService: e.target.value,
                      })
                    }
                    className="min-h-[200px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    HTML content for the terms of service page. Include proper HTML tags for formatting.
                  </p>
                </div>

                {/* Refund Policy */}
                <div className="space-y-2">
                  <Label htmlFor="refund_policy">Refund Policy</Label>
                  <Textarea
                    id="refund_policy"
                    placeholder="Enter your refund policy content here..."
                    value={legalSettings.refundPolicy}
                    onChange={(e) =>
                      setLegalSettings({
                        ...legalSettings,
                        refundPolicy: e.target.value,
                      })
                    }
                    className="min-h-[200px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    HTML content for the refund policy page. Include proper HTML tags for formatting.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* General Settings Tab */}
          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Additional application settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  General settings will be available here in future updates.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
}
